import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import classnames from 'classnames';
import get from 'lodash/get';

import { canPlayVideo } from '../../redux/env';

import { FullStretchPreloadedImage } from './PreloadedImage';

import { DESKTOP_START_QUERY } from '../../styling/mixins/responsive';

const ObserverWrapperStyles = `width: 100%; position: relative; overflow: hidden;`;
const ObserverWrapper = styled(InView)`
  ${ObserverWrapperStyles};

  ${({ aspectratio }) => {
    return !!aspectratio
      ? `
      padding-bottom: ${aspectratio};
      position: relative;
    `
      : ``;
  }}
`;

const AspectRatioPusher = styled.div`
  ${({ mobileAspectRatio, desktopAspectRatio }) => `
    ${ObserverWrapperStyles};
    height: calc(100vw*${parseFloat(mobileAspectRatio) / 100});

    @media(${DESKTOP_START_QUERY}) {
      padding-top: ${desktopAspectRatio};
    }
  `}
`;

// font-family is a IE11 hack related to the ObjectFit polyfill
const ObjectPositionStyles = `
  .media--size-cover & {
    object-position: center center;
    object-fit: cover;
    font-family: 'object-position: center; object-fit: cover;'
  }

  .media--size-cover.media--layout-left & {
    object-position: left top;
    font-family: 'object-position: left; object-fit: cover;'
  }

  .media--size-cover.media--layout-right & {
    object-position: right top;
    font-family: 'object-position: right; object-fit: cover;'
  }

  .media--size-contain & {
    object-position: center center;
    object-fit: contain;
    font-family: 'object-position: center;';
  }
`;

// padding: inherit; to get padding as this is absolutely positioned.
const PositionedPoster = styled(FullStretchPreloadedImage)`
  padding: inherit;
  ${ObjectPositionStyles}
`;

// relative (non-static) positioning allows its to stack with the PreloadImage
const VideoContent = styled.video`
  position: relative;
  display: block;
  margin: auto;
  width: 100%;
  height: 100%;
  background-color: transparent;

  .media--layout-left &,
  .media--layout-right & {
    display: block;
    margin: 0;
  }

  .media--forced-aspect-ratio & {
    position: absolute;
    top: 0;
    left: 0;
  }

  ${ObjectPositionStyles}
`;

// Ordered based on expected firing order -  https://www.w3schools.com/tags/av_event_loadeddata.asp
const PLAYBACK_SUCCESSFUL_EVENTS = [
  'loadstart',
  'durationchange',
  'loadeddata',
  'playing',
  'timeupdate',
];

const PLAYBACK_FAILED_EVENTS = ['error'];

function getVideoSourceFromProps(props) {
  let { isMobile = true, video = '', mobileVideo = '' } = props;

  return isMobile ? mobileVideo : video;
}

export class AutoplayVideo extends Component {
  static propTypes = {
    poster: PropTypes.string,
    mobileVideo: PropTypes.string,
    video: PropTypes.string,
    layout: PropTypes.oneOf(['left', 'right', 'center', 'custom']),
    imageSize: PropTypes.string,
    isMobile: PropTypes.bool.isRequired,
    canPlayVideo: PropTypes.oneOf([true, false, 'unknown']),
    canPlayVideoDispatch: PropTypes.func,
    isPreloadedCallback: PropTypes.func,
    loop: PropTypes.bool,
  };

  static defaultProps = {
    imageSize: 'cover',
    layout: 'center',
    loop: true,
  };

  constructor(props) {
    super(props);

    // Fixing Autobinding issue -
    // https://reactjs.org/docs/react-without-es6.html#autobinding
    this.playbackSuccessful = this.playbackSuccessful.bind(this);
    this.playbackFailed = this.playbackFailed.bind(this);
    this.clearBrowserCanPlayListeners = this.clearBrowserCanPlayListeners.bind(
      this,
    );
  }

  componentDidMount() {
    if (!this.media.crossorigin) {
      this.media.setAttribute('crossorigin', 'anonymous');
    }
    if (!this.media.muted) {
      this.media.setAttribute('muted', '');
      this.media.setAttribute('defaultMuted', '');
    }

    this.media.disableRemotePlayback = true;
  }

  componentDidUpdate(prevProps) {
    // If the video tag's source tag changed, we need to remind it to reload its contents
    if (
      getVideoSourceFromProps(this.props) !== getVideoSourceFromProps(prevProps)
    ) {
      // load() rejects all currently pending play promises and attempts to follow the "preload" rules
      this.media.load();
      // instead of play(), should readd IntersectionObserver listeners instead so that we don't load/play inactive videos...
      this.media.play();
    }
  }

  componentWillUnmount() {
    this.clearBrowserCanPlayListeners();
  }

  attemptPlayback() {
    // Add listeners if we don't know whether we can play video yet
    if (this.props.canPlayVideo === 'unknown') {
      this.addBrowserCanPlayListeners();
    }

    let playbackPromise = this.media.play();

    // Modern browsers return a promise from video.play() that is rejected if it does not begin playback
    // This can cause false positives, but is generally acceptable for the first play on the page
    if (playbackPromise !== undefined) {
      playbackPromise.catch(error => {
        this.playbackFailed('play() promise rejected:', error, error.message);
      });
    }
  }

  pausePlayback = () => {
    // Only pause if this video has done anything to load already.  Otherwise could interfere with autoplay.
    // 0 === HAVE_NOTHING === has not received any video metadata or data.  Has not attempted load yet.
    if (this.media.readyState > 0) {
      this.media.pause();
    }
  };

  addBrowserCanPlayListeners() {
    const { media, source } = this;

    if (media) {
      PLAYBACK_SUCCESSFUL_EVENTS.forEach(eventName => {
        media.addEventListener(eventName, this.playbackSuccessful);
      });
      PLAYBACK_FAILED_EVENTS.forEach(eventName => {
        media.addEventListener(eventName, this.playbackFailed);
        source.addEventListener(eventName, this.clearBrowserCanPlayListeners);
      });

      this.playbackStartedTimer = setTimeout(() => {
        this.playbackFailed('playback timed out');
      }, 3000);
    }
  }

  clearBrowserCanPlayListeners() {
    const { media, source, playbackStartedTimer } = this;

    if (media) {
      PLAYBACK_SUCCESSFUL_EVENTS.forEach(eventName => {
        media.removeEventListener(eventName, this.playbackSuccessful);
      });
      PLAYBACK_FAILED_EVENTS.forEach(eventName => {
        media.removeEventListener(eventName, this.playbackFailed);
        source.removeEventListener(
          eventName,
          this.clearBrowserCanPlayListeners,
        );
      });
    }

    clearTimeout(playbackStartedTimer);
  }

  playbackSuccessful(event) {
    this.clearBrowserCanPlayListeners();
    if (this.props.canPlayVideo === 'unknown' || !this.props.canPlayVideo) {
      this.props.canPlayVideoDispatch(true);
    }
  }

  playbackFailed(reason) {
    this.clearBrowserCanPlayListeners();
    // String from a handler or ERROR received from media or source
    this.fallbackToImage(
      typeof reason === 'string' ? reason : 'failed event: ' + reason.type,
    );
  }

  fallbackToImage(reason) {
    if (this.props.canPlayVideo === 'unknown') {
      // Keep this for debugging purposes when there are issues on the page?
      console.log('canPlayVideo is FALSE. Reason is:', reason);
      this.props.canPlayVideoDispatch(false);
    }
  }

  render() {
    const {
      poster,
      canPlayVideo,
      className,
      layout,
      imageSize,
      isMobile,
      loop,
      aspectRatio: desktopAspectRatio,
      mobileAspectRatio,
      isPreloadedCallback,
    } = this.props;

    let forcedAspectRatio = isMobile ? mobileAspectRatio : desktopAspectRatio;
    let classNamesList = classnames(className, 'video-container', {
      'media--forced-aspect-ratio': forcedAspectRatio,
      'media--size-cover': imageSize === 'cover',
      'media--size-contain': imageSize !== 'cover',
      'media--layout-left': layout === 'left',
      'media--layout-right': layout === 'right',
    });

    // this is considered to be "preloaded" when we have content to show the user via the fake poster
    return (
      <ObserverWrapper
        className={classNamesList}
        threshold={0.5}
        triggerOnce={
          false /***  MUST BE SET EXPLICITLY.  Default is not respected in iOS. ***/
        }
        onChange={inView => {
          if (inView) {
            this.attemptPlayback();
          } else if (canPlayVideo === true) {
            // If canPlayVideo is unknown, we don't want to pause and interrupt initial play.
            this.pausePlayback();
          }
        }}
      >
        {forcedAspectRatio && (
          <AspectRatioPusher
            mobileAspectRatio={mobileAspectRatio}
            desktopAspectRatio={desktopAspectRatio}
          />
        )}
        <PositionedPoster
          className="video-poster"
          imageSource={poster}
          loadedCallback={isPreloadedCallback}
        />
        <VideoContent
          className="video-player"
          crossOrigin="anonymous"
          loop={loop}
          muted={true}
          defaultMuted={true}
          playsInline={true}
          preload="none"
          layout={layout}
          hasAspectRatio={isMobile ? !!mobileAspectRatio : !!desktopAspectRatio}
          imageSize={imageSize}
          x-webkit-airplay="deny"
        >
          <source
            ref={el => {
              if (el !== null) {
                this.media = el.parentElement;
                this.source = el;
              }
            }}
            src={getVideoSourceFromProps(this.props)}
            type="video/mp4"
          />
        </VideoContent>
      </ObserverWrapper>
    );
  }
}

const mapDispatchToProps = {
  canPlayVideoDispatch: canPlayVideo,
};

export default connect(null, mapDispatchToProps)(AutoplayVideo);
